import { NavLink, useLocation } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useMsal } from '@azure/msal-react'
import {
  LayoutDashboard,
  BookOpen,
  Users,
  FileText,
  Settings,
  Building2,
  Plug,
  Package,
  ClipboardCheck,
  LogOut,
  User,
} from 'lucide-react'
import { cn } from '../../lib/utils'
import { useAuthStore } from '../../stores/auth'
import { api } from '../../lib/api'

const roleBadgeColors: Record<string, string> = {
  admin: 'bg-purple-100 text-purple-700',
  sales: 'bg-blue-100 text-blue-700',
  engineer: 'bg-green-100 text-green-700',
  viewer: 'bg-gray-100 text-gray-700',
}

const navItems = [
  { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { to: '/scopes', label: 'Proposals', icon: FileText },
  { to: '/catalog', label: 'Service Catalog', icon: BookOpen },
  { to: '/clients', label: 'Companies', icon: Building2 },
  { to: '/products', label: 'Product Catalog', icon: Package },
]

const adminNavItems = [
  { to: '/integrations', label: 'Integrations', icon: Plug },
  { to: '/users', label: 'Users', icon: Users },
  { to: '/settings', label: 'Settings', icon: Settings },
]

export default function Sidebar() {
  const location = useLocation()
  const { user } = useAuthStore()
  const { instance } = useMsal()

  const isActive = (to: string) =>
    location.pathname === to || location.pathname.startsWith(to + '/')

  const isReviewer = user?.role === 'admin' || user?.role === 'engineer'

  const { data: pendingData } = useQuery({
    queryKey: ['approvals', 'pending-mine'],
    queryFn: () => api.get<{ data: unknown[] }>('/approvals/pending-mine'),
    enabled: isReviewer,
    refetchInterval: 10_000,
  })
  const pendingCount = pendingData?.data?.length ?? 0

  return (
    <aside className="flex w-64 flex-col border-r bg-card">
      {/* Brand header */}
      <div className="flex h-28 items-center justify-center border-b px-4">
        <img src="/scopeassistant-horizontal.svg" alt="ScopeAssistant" className="h-20 w-auto object-contain" />
      </div>

      {/* Navigation */}
      <nav className="flex-1 space-y-1 p-3">
        {navItems.map(({ to, label, icon: Icon }) => (
          <NavLink
            key={to}
            to={to}
            className={cn(
              'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
              isActive(to)
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
            )}
          >
            <Icon className="h-4 w-4 shrink-0" />
            {label}
          </NavLink>
        ))}

        {isReviewer && (
          <>
            <div className="my-2 h-px bg-border" />
            <NavLink
              to="/reviews"
              className={cn(
                'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                isActive('/reviews')
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
              )}
            >
              <ClipboardCheck className="h-4 w-4 shrink-0" />
              My Reviews
              {pendingCount > 0 && (
                <span
                  className={cn(
                    'ml-auto flex h-5 min-w-5 items-center justify-center rounded-full px-1.5 text-xs font-medium',
                    isActive('/reviews')
                      ? 'bg-primary-foreground text-primary'
                      : 'bg-primary text-primary-foreground'
                  )}
                >
                  {pendingCount}
                </span>
              )}
            </NavLink>
          </>
        )}

        {user?.role === 'admin' && (
          <>
            <div className="my-2 h-px bg-border" />
            {adminNavItems.map(({ to, label, icon: Icon }) => (
              <NavLink
                key={to}
                to={to}
                className={cn(
                  'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                  isActive(to)
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                )}
              >
                <Icon className="h-4 w-4 shrink-0" />
                {label}
              </NavLink>
            ))}
          </>
        )}
      </nav>

      {/* Copyright */}
      <div className="px-4 pb-1 text-center">
        <p className="text-[10px] text-muted-foreground">© {new Date().getFullYear()} Raven Automation, Inc.</p>
      </div>

      {/* User footer */}
      {user && (
        <div className="border-t p-3">
          <div className="flex items-center gap-2.5 rounded-md px-2 py-2">
            <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-muted">
              <User className="h-3.5 w-3.5 text-muted-foreground" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-xs font-medium text-foreground">{user.displayName || user.email}</p>
              <div className="flex items-center gap-1.5">
                <span className={cn('rounded-full px-1.5 py-px text-xs font-medium', roleBadgeColors[user.role] ?? 'bg-gray-100 text-gray-700')}>
                  {user.role}
                </span>
              </div>
            </div>
            <button
              onClick={() => instance.logoutRedirect().catch(console.error)}
              className="shrink-0 rounded p-1 text-muted-foreground hover:bg-accent hover:text-foreground"
              title="Sign out"
            >
              <LogOut className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      )}
    </aside>
  )
}
